import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Badge,
  InputBase,
  Button,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { getCategories } from '../../services/api';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: 25,
  backgroundColor: alpha(theme.palette.common.white, 0.9),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 1),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
  border: '1px solid #ddd',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#666',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: '#333',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.2, 1, 1.2, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '30ch',
    },
  },
}));

const Navbar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { isAuthenticated, user, logout, openLoginModal } = useAuth();
  const { getCartCount, wishlistItems } = useCart();

  const [searchQuery, setSearchQuery] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getCategories();
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/filter?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setMobileSearchOpen(false);
    }
  };

  const handleMobileSearchClick = () => {
    setMobileSearchOpen(!mobileSearchOpen);
  };

  const handleMobileSearchClose = () => {
    setMobileSearchOpen(false);
    setSearchQuery('');
  };

  const handleProfileClick = (event) => {
    if (isAuthenticated) {
      setAnchorEl(event.currentTarget);
    } else {
      openLoginModal();
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/');
  };

  const menuItems = [
    { label: 'My Profile', icon: <AccountCircleIcon />, action: () => { navigate('/profile'); handleMenuClose(); } },
    { label: 'My Orders', icon: <ShoppingBagIcon />, action: () => { navigate('/orders'); handleMenuClose(); } },
    { label: 'Wishlist', icon: <FavoriteIcon />, action: () => { navigate('/wishlist'); handleMenuClose(); } },
    { label: 'Logout', icon: <LogoutIcon />, action: handleLogout },
  ];

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: '#fff',
          color: '#333',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 0.5 }}>
          {/* Left Section - Menu & Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              onClick={() => navigate('/')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: '#8B4513',
                  fontFamily: 'Georgia, serif',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Nut Nutritions
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: '#8B4513',
                  fontFamily: 'Georgia, serif',
                  display: { xs: 'block', sm: 'none' },
                }}
              >
                NN
              </Typography>
            </Box>
          </Box>

          {/* Center - Search (Desktop) */}
          {!isMobile && (
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search for dry fruits, nuts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearch}
              />
            </Search>
          )}

          {/* Right Section - Icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            {isMobile && (
              <IconButton onClick={handleMobileSearchClick}>
                <SearchIcon />
              </IconButton>
            )}

            <IconButton onClick={() => navigate('/wishlist')}>
              <Badge badgeContent={wishlistItems.length} color="error">
                <FavoriteIcon sx={{ color: '#666' }} />
              </Badge>
            </IconButton>

            <IconButton onClick={() => navigate('/cart')}>
              <Badge badgeContent={getCartCount()} color="error">
                <ShoppingCartIcon sx={{ color: '#666' }} />
              </Badge>
            </IconButton>

            {isAuthenticated ? (
              <Button
                onClick={handleProfileClick}
                startIcon={<PersonIcon />}
                sx={{
                  color: '#333',
                  textTransform: 'none',
                  display: { xs: 'none', sm: 'flex' },
                }}
              >
                {user?.name || 'Profile'}
              </Button>
            ) : (
              <Button
                onClick={openLoginModal}
                variant="contained"
                sx={{
                  bgcolor: '#8B4513',
                  '&:hover': { bgcolor: '#6d3610' },
                  textTransform: 'none',
                  display: { xs: 'none', sm: 'flex' },
                }}
              >
                Login
              </Button>
            )}

            {isMobile && (
              <IconButton onClick={handleProfileClick}>
                <PersonIcon sx={{ color: '#666' }} />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { mt: 1, minWidth: 200, borderRadius: 2 },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Hello,
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold">
            {user?.name}
          </Typography>
        </Box>
        <Divider />
        {menuItems.map((item, index) => (
          <MenuItem
            key={index}
            onClick={item.action}
            sx={{ gap: 1.5, py: 1.2 }}
          >
            {item.icon}
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Side Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 280, height: '100%', bgcolor: '#faf8f5' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              bgcolor: '#8B4513',
              color: '#fff',
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Menu
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate('/');
                  setDrawerOpen(false);
                }}
              >
                <ListItemText primary="Home" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate('/filter/All');
                  setDrawerOpen(false);
                }}
              >
                <ListItemText primary="Shop All" />
              </ListItemButton>
            </ListItem>
          </List>

          <Divider />

          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
              CATEGORIES
            </Typography>
          </Box>

          <List>
            {categories.map((category) => (
              <ListItem key={category._id} disablePadding>
                <ListItemButton
                  onClick={() => {
                    navigate(`/filter/${category._id}`);
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemText primary={category.CategoryName} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider />

          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate('/');
                  setDrawerOpen(false);
                }}
              >
                <ListItemText primary="About Us" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate('/contact');
                  setDrawerOpen(false);
                }}
              >
                <ListItemText primary="Contact" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Mobile Search Overlay */}
      {isMobile && mobileSearchOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            pt: 8,
            px: 2,
            animation: 'fadeIn 0.3s ease-in',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
          onClick={handleMobileSearchClose}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '600px',
              bgcolor: '#fff',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              animation: 'slideDown 0.3s ease-out',
              '@keyframes slideDown': {
                from: {
                  transform: 'translateY(-20px)',
                  opacity: 0,
                },
                to: {
                  transform: 'translateY(0)',
                  opacity: 1,
                },
              },
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                borderBottom: '1px solid #eee',
              }}
            >
              <Search
                sx={{
                  flex: 1,
                  mr: 1,
                  borderRadius: 2,
                }}
              >
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder="Search for dry fruits, nuts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearch}
                  autoFocus
                  sx={{
                    '& .MuiInputBase-input': {
                      width: '100%',
                    },
                  }}
                />
              </Search>
              <IconButton
                onClick={handleMobileSearchClose}
                sx={{
                  color: '#666',
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            {searchQuery && (
              <Box
                sx={{
                  p: 2,
                  borderTop: '1px solid #eee',
                  bgcolor: '#fafafa',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Press Enter to search
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Spacer for fixed navbar */}
      <Toolbar />
    </>
  );
};

export default Navbar;
